// ─── SparePartsCustomers.jsx ────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Chip, Card, CardContent } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { spAPI } from '../../services/api';
import DataTable, { StatusChip } from '../../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = n => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(n||0);

const CustomerForm = ({ open, onClose, customer, onSaved }) => {
  const { register, handleSubmit, reset } = useForm();
  const [loading, setLoading] = useState(false);
  useEffect(() => { reset(customer || { customer_type: 'retail', discount_percent: 0 }); }, [customer, open, reset]);
  const onSubmit = async (data) => {
    try { setLoading(true);
      if (customer?.id) {
        await spAPI.updateCustomer(customer.id, data);
        toast.success('Updated');
        onSaved({ ...customer, ...data });
      } else {
        const response = await spAPI.createCustomer(data);
        toast.success('Customer added');
        onSaved(response.data.data);
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{customer?.id ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Customer Name *" {...register('name', { required: true })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Email" {...register('email')} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Address" {...register('address')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="City" {...register('city')} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer Type</InputLabel>
                <Select {...register('customer_type')} label="Customer Type" defaultValue="retail">
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="wholesale">Wholesale</MenuItem>
                  <MenuItem value="workshop">Workshop</MenuItem>
                  <MenuItem value="fleet">Fleet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}><TextField fullWidth label="Discount %" type="number" {...register('discount_percent', { min: 0, max: 100, valueAsNumber: true })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Credit Limit ($)" type="number" {...register('credit_limit', { valueAsNumber: true })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export const SparePartsCustomers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]); const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0); const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(''); const [formOpen, setFormOpen] = useState(false); const [editCustomer, setEditCustomer] = useState(null);
  const [redirectContext, setRedirectContext] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try { setLoading(true);
      const { data } = await spAPI.getCustomers({ page: page+1, limit: rowsPerPage, search: search||undefined });
      setCustomers(data.data||[]); setTotal(data.pagination?.total||0);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [page, rowsPerPage, search]);
  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    if (!location.state?.openCreateCustomer) return;

    setRedirectContext({
      returnTo: location.state.returnTo || null,
      returnSystem: location.state.returnSystem || 'spare_parts',
      reopenOrder: location.state.reopenOrder !== false,
    });
    setEditCustomer(null);
    setFormOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const handleCustomerSaved = async (savedCustomer) => {
    await fetchCustomers();

    if (redirectContext?.returnTo && savedCustomer?.id) {
      localStorage.setItem('activeUnit', redirectContext.returnSystem);
      navigate(redirectContext.returnTo, {
        replace: true,
        state: {
          openCreateOrder: redirectContext.reopenOrder,
          createdCustomer: savedCustomer,
          returnSystem: redirectContext.returnSystem,
        },
      });
      setRedirectContext(null);
    }
  };

  const columns = [
    { key: 'customer_code', label: 'Code', render: v => <Typography fontFamily="monospace" fontWeight={600} fontSize="0.8rem">{v}</Typography> },
    { key: 'name', label: 'Name', render: v => <Typography fontWeight={600}>{v}</Typography> },
    { key: 'phone', label: 'Phone' }, { key: 'city', label: 'City' },
    { key: 'customer_type', label: 'Type', render: v => <Chip label={v} size="small" color={v==='wholesale'?'primary':v==='workshop'?'info':'default'} sx={{ fontSize: '0.7rem', fontWeight: 700 }} /> },
    { key: 'discount_percent', label: 'Discount', render: v => v > 0 ? `${v}%` : '—' },
    { key: 'total_orders', label: 'Orders', align: 'right' },
    { key: 'total_spent', label: 'Total Spent', align: 'right', render: v => <Typography fontWeight={700} color="#d97706">{fmt(v)}</Typography> },
    { key: 'is_active', label: 'Status', render: v => <StatusChip status={v ? 'active' : 'inactive'} /> },
    { key: 'id', label: '', render: (v, row) => <Button size="small" onClick={() => { setEditCustomer(row); setFormOpen(true); }}>Edit</Button> },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>Customers</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditCustomer(null); setFormOpen(true); }}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>Add Customer</Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <TextField placeholder="Search customers..." size="small" sx={{ minWidth: 300 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataTable columns={columns} rows={customers} loading={loading} total={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </CardContent>
      </Card>
      <CustomerForm open={formOpen} onClose={() => setFormOpen(false)} customer={editCustomer} onSaved={handleCustomerSaved} />
    </Box>
  );
};

export default SparePartsCustomers;
